import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { Product } from '../types';
import { productService } from '../services/productService';

export const Store = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'merch' | 'drinks' | 'packs'>('all');
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const fetchedProducts = await productService.getAllProducts();
        setProducts(fetchedProducts);
      } catch (err) {
        setError('Erreur lors du chargement des produits');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 min-h-screen bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-gradient-to-br from-orange-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Boutique</h1>
        
        {/* Category Filter */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedCategory === 'all' 
                ? 'bg-pink-500 text-white' 
                : 'bg-white text-gray-700 hover:bg-pink-100'
            }`}
          >
            Tout
          </button>
          <button
            onClick={() => setSelectedCategory('merch')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedCategory === 'merch' 
                ? 'bg-pink-500 text-white' 
                : 'bg-white text-gray-700 hover:bg-pink-100'
            }`}
          >
            Merchandising
          </button>
          <button
            onClick={() => setSelectedCategory('drinks')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedCategory === 'drinks' 
                ? 'bg-pink-500 text-white' 
                : 'bg-white text-gray-700 hover:bg-pink-100'
            }`}
          >
            Forfaits Boissons
          </button>
          <button
            onClick={() => setSelectedCategory('packs')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedCategory === 'packs' 
                ? 'bg-pink-500 text-white' 
                : 'bg-white text-gray-700 hover:bg-pink-100'
            }`}
          >
            Packs
          </button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts
            .filter(product => product.is_available)
            .map((product) => (
              <div key={product.id} className="bg-white rounded-xl shadow-lg overflow-hidden transform hover:scale-105 transition-transform">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <div className="flex justify-between items-center">
                    {product.category === 'packs' && product.base_price ? (
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl font-bold text-pink-500">{product.price}€</span>
                          <span className="text-lg text-gray-500 line-through">{product.base_price}€</span>
                        </div>
                        <p className="text-sm text-green-600">
                          Économisez {(product.base_price - product.price).toFixed(2)}€
                        </p>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold text-pink-500">{product.price}€</span>
                    )}
                    <div className="space-x-2">
                      <a 
                        href={`/product/${product.id}`}
                        className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Détails
                      </a>
                      <button
                        onClick={() => addToCart(product, 1)}
                        className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {filteredProducts.filter(product => product.is_available).length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun produit disponible dans cette catégorie</p>
          </div>
        )}
      </div>
    </div>
  );
}